import PricingEntity from '../entities/Pricing';
import ProductEntity, { ProductJSON } from '../entities/Product';
import { sortBy } from './arrays';

export function computePriceInclFees({ fixedPriceInclFees, minSpentInclFees, pricings }: ProductJSON, quantity: number) {
  const pricing = sortBy(pricings, 'threshold').find(({ threshold }) => quantity <= threshold);

  if (!pricing) {
    throw new Error('Missing pricing');
  }

  const price = fixedPriceInclFees + pricing.groupPriceInclFees + (quantity * pricing.unitPriceInclFees);

  if (price < minSpentInclFees) {
    return minSpentInclFees;
  }

  return price;
}

export function computePriceExclFees({ fixedPrice, minSpent, pricings }: ProductEntity, quantity: number) {
  const pricing = sortBy(pricings, 'threshold').find(({ threshold }) => quantity <= threshold);

  if (!pricing) {
    throw new Error('Missing pricing');
  }

  const price = fixedPrice + pricing.groupPrice + (quantity * pricing.unitPrice);

  if (price < minSpent) {
    return minSpent;
  }

  return price;
}

export function getMaxThreshold({ pricings }: Pick<ProductJSON, 'pricings'>) {
  return Math.max(...pricings.map(({ threshold }) => threshold));
}

export function getHighestThresholdPricing({ pricings }: Pick<ProductJSON, 'pricings'>) {
  return pricings.sort((a,b) => b.threshold - a.threshold)[0];
}

export function isParticipantsRangeValid(product: Pick<ProductJSON, 'pricings'>, value: number): boolean {
  const maxThreshold = getMaxThreshold(product);

  return value > 0 && value <= maxThreshold;
}

export function getMinPricePerUnit({ minSpentInclFees, fixedPriceInclFees, pricings }: Pick<ProductEntity, 'minSpentInclFees' | 'fixedPriceInclFees'> & { pricings: Pick<PricingEntity, 'threshold' | 'groupPriceInclFees' | 'unitPriceInclFees'>[] }) {
  return Math.min(
    ...pricings.map(({ threshold, groupPriceInclFees, unitPriceInclFees }) => {
      const price = fixedPriceInclFees + groupPriceInclFees + (threshold * unitPriceInclFees);

      return (price < minSpentInclFees ? minSpentInclFees : price) / threshold;
    })
  );
}

export function getCheapestPackageUnitPrice(products: ProductJSON[], quantity: number) {
  return Math.min(
    ...products
      .filter(product => isParticipantsRangeValid(product, quantity))
      .map(product => computePriceInclFees(product, quantity) / quantity)
  );
}

export function isCustomRequestProduct(product?: ProductJSON) {
  if (!product || product.pricings.length === 0) {
    return false;
  }

  return product.pricings.every(({ groupPriceInclFees, unitPriceInclFees }) => groupPriceInclFees === 0 && unitPriceInclFees === 0);
}

export function getProductPrice(product: ProductJSON, quantity: number) {
  if (!isParticipantsRangeValid(product, quantity)) {
    return null;
  }

  return computePriceInclFees(product, quantity);
}

export function getPackagePrice(product: ProductJSON, quantity: number) {
  const maxThreshold = getMaxThreshold(product);

  if (quantity > maxThreshold) {
    return getProductPrice(product, maxThreshold);
  }

  if (quantity < 1) {
    return getProductPrice(product, 1);
  }

  return getProductPrice(product, quantity);
}

export function sortByPackagePrice(products: ProductJSON[], quantity: number) {
  return [...products].sort((a, b) => {
    const priceA = getPackagePrice(a, quantity) ?? 0;
    const priceB = getPackagePrice(b, quantity) ?? 0;

    if (priceA === 0) {
      return 1;
    }

    if (priceB === 0) {
      return -1;
    }

    return priceA - priceB;
  });
}

export function getProductWithMinUnitPrice(products: ProductJSON[]): ProductJSON {
  return products.reduce((out, product) => product.minPricePerUnit < out.minPricePerUnit ? product : out, products[0]);
}